import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Package, MapPin, Barcode, Calendar, Save, Trash2, Move, Building2, Tag, Bookmark, X, AlertCircle, ArrowUpFromLine, ArrowDownToLine, Check, ChevronsUpDown, Truck, Clock, ShoppingBag, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";
import { dualPriceText } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InventoryDetailPage() {
  const { user } = useAuth();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [showPurchasePrices, setShowPurchasePrices] = useState(() => localStorage.getItem('show_purchase_prices') === '1');
  const [detailPrices, setDetailPrices] = useState([]);
  const [detailSuppliers, setDetailSuppliers] = useState([]);

  useEffect(() => {
    localStorage.setItem('show_purchase_prices', showPurchasePrices ? '1' : '0');
    if (showPurchasePrices && item?.ean) {
      Promise.all([
        axios.get(`${API}/purchasing/prices/by-ean/${item.ean}`).then(r => r.data?.prices || []).catch(() => []),
        detailSuppliers.length ? Promise.resolve(detailSuppliers) : axios.get(`${API}/purchasing/suppliers`).then(r => r.data || []).catch(() => [])
      ]).then(([prices, sups]) => { setDetailPrices(prices); setDetailSuppliers(sups); });
    }
  }, [showPurchasePrices, item?.ean]); // eslint-disable-line
  const [productSummary, setProductSummary] = useState(null);
  const [stockStatus, setStockStatus] = useState(null);
  const [lastDeliveryDate, setLastDeliveryDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Move dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveFromLocation, setMoveFromLocation] = useState("");
  const [moveToLocation, setMoveToLocation] = useState("");
  const [moveQuantity, setMoveQuantity] = useState(1);
  const [maxMoveQuantity, setMaxMoveQuantity] = useState(1);
  const [moveToLocationOpen, setMoveToLocationOpen] = useState(false);
  const [recentLocations, setRecentLocations] = useState(() => {
    try {
      const saved = localStorage.getItem('recent_scan_locations');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Booking dialog state
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [bookQuantity, setBookQuantity] = useState(1);
  const [bookComment, setBookComment] = useState("");
  const [bookLocation, setBookLocation] = useState("");
  
  // Scan out dialog state
  const [showScanOutDialog, setShowScanOutDialog] = useState(false);
  const [scanOutLocation, setScanOutLocation] = useState("");
  const [scanOutQuantity, setScanOutQuantity] = useState(1);
  const [maxScanOutQuantity, setMaxScanOutQuantity] = useState(1);
  
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Combobox open states
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [manufacturerOpen, setManufacturerOpen] = useState(false);
  
  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [showReservationsDialog, setShowReservationsDialog] = useState(false);
  const [fixingReservations, setFixingReservations] = useState(false);

  // Check if there's a reservation bug (reservations exist but should have been converted)
  const actualBooked = productSummary?.total_booked || stockStatus?.booked || 0;
  const actualAvailable = Math.max(0, (productSummary?.total_quantity || productSummary?.total_antal || 0) - actualBooked);
  const hasReservationBug = reservations.length > 0 && 
    (productSummary?.total_quantity || productSummary?.total_antal) > 0 && 
    actualAvailable > 0;

  useEffect(() => {
    fetchItem();
    fetchFilters();
  }, [itemId]);
  
  // Handle action parameter (e.g., ?action=move)
  useEffect(() => {
    if (item && productSummary && searchParams.get('action') === 'move') {
      // Clear the param first
      setSearchParams({});
      // Sort locations by quantity (descending) and pick the one with most stock
      if (productSummary.locations.length > 0) {
        const sortedLocations = [...productSummary.locations].sort((a, b) => b.antal - a.antal);
        const bestLocation = sortedLocations[0];
        
        setMoveFromLocation(bestLocation.lagerplats);
        setMaxMoveQuantity(bestLocation.antal);
        setMoveQuantity(1);
      } else {
        setMoveFromLocation("");
        setMoveQuantity(1);
        setMaxMoveQuantity(1);
      }
      setMoveToLocation("");
      setShowMoveDialog(true);
    }
  }, [item, productSummary, searchParams, setSearchParams]);

  const fetchFilters = async () => {
    try {
      const [catRes, mfgRes, locRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/manufacturers`),
        axios.get(`${API}/locations`)
      ]);
      setCategories(catRes.data);
      setManufacturers(mfgRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/inventory/${itemId}`);
      setItem(response.data);
      setFormData(response.data);
      
      // Fetch product summary with all locations and bookings
      if (response.data.ean) {
        const [summaryRes, stockStatusRes, historyRes, reservationsRes] = await Promise.all([
          axios.get(`${API}/inventory/product-summary/${response.data.ean}`),
          axios.get(`${API}/purchasing/stock-status/${response.data.ean}`).catch(() => ({ data: null })),
          axios.get(`${API}/history/logs?search=${response.data.ean}&action_type=IN&limit=1`).catch(() => ({ data: { logs: [] } })),
          axios.get(`${API}/purchasing/reservations/by-ean/${response.data.ean}`).catch(() => ({ data: { reservations: [] } }))
        ]);
        setProductSummary(summaryRes.data);
        setStockStatus(stockStatusRes.data);
        setReservations(reservationsRes.data?.reservations || []);
        
        // Get last delivery date from history
        if (historyRes.data?.logs?.length > 0) {
          setLastDeliveryDate(historyRes.data.logs[0].timestamp);
        } else {
          setLastDeliveryDate(null);
        }
      }
    } catch (error) {
      console.error("Error fetching item:", error);
      toast.error("Kunde inte ladda produkten");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/${itemId}`, formData);
      toast.success("Produkten uppdaterad");
      setEditMode(false);
      fetchItem();
      fetchFilters();
    } catch (error) {
      toast.error("Kunde inte spara: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/inventory/product/${item.ean}`);
      toast.success("Produkten har raderats permanent");
      navigate("/inventory");
    } catch (error) {
      toast.error("Kunde inte radera: " + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/inventory/${itemId}`);
      toast.success("Produkten borttagen");
      navigate("/inventory");
    } catch (error) {
      toast.error("Kunde inte ta bort: " + (error.response?.data?.detail || error.message));
    }
  };

  // Fix reservation bug - manually trigger auto-conversion
  const handleFixReservations = async () => {
    if (!item?.ean) return;
    
    setFixingReservations(true);
    try {
      const location = productSummary?.locations?.[0]?.lagerplats || "Okänd";
      const response = await axios.post(
        `${API}/purchasing/reservations/auto-convert/${item.ean}`,
        null,
        { params: { location } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchItem(); // Refresh to update reservations and bookings
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Kunde inte konvertera: " + (error.response?.data?.detail || error.message));
    } finally {
      setFixingReservations(false);
    }
  };

  const openMoveDialog = () => {
    if (productSummary && productSummary.locations.length > 0) {
      // Only use locations with stock for "from"
      const withStock = productSummary.locations.filter(l => (l.antal || 0) > 0);
      if (withStock.length > 0) {
        const sortedLocations = [...withStock].sort((a, b) => b.antal - a.antal);
        const bestLocation = sortedLocations[0];
        setMoveFromLocation(bestLocation.lagerplats);
        setMaxMoveQuantity(bestLocation.antal);
      } else {
        setMoveFromLocation("");
        setMaxMoveQuantity(0);
      }
      setMoveQuantity(1);
    } else {
      setMoveFromLocation("");
      setMoveQuantity(1);
      setMaxMoveQuantity(1);
    }
    setMoveToLocation("");
    setShowMoveDialog(true);
  };

  const handleFromLocationChange = (location) => {
    setMoveFromLocation(location);
    const loc = productSummary?.locations.find(l => l.lagerplats === location);
    if (loc) {
      setMaxMoveQuantity(loc.antal);
      setMoveQuantity(Math.min(moveQuantity, loc.antal));
    }
  };

  const handleMove = async () => {
    if (!moveFromLocation.trim()) {
      toast.error("Välj lagerplats att flytta från");
      return;
    }
    if (!moveToLocation.trim()) {
      toast.error("Ange ny lagerplats");
      return;
    }
    if (moveFromLocation === moveToLocation) {
      toast.error("Kan inte flytta till samma plats");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/inventory/move-by-ean?ean=${item.ean}`, {
        from_location: moveFromLocation,
        to_location: moveToLocation,
        quantity: moveQuantity
      });
      toast.success(response.data.message);
      
      // Save destination as recent location
      try {
        const saved = localStorage.getItem('recent_scan_locations');
        let recent = saved ? JSON.parse(saved) : [];
        recent = [moveToLocation, ...recent.filter(l => l !== moveToLocation)].slice(0, 10);
        localStorage.setItem('recent_scan_locations', JSON.stringify(recent));
        setRecentLocations(recent);
      } catch (e) {
        console.error("Could not save recent location:", e);
      }
      
      setShowMoveDialog(false);
      fetchItem();
    } catch (error) {
      toast.error("Kunde inte flytta: " + (error.response?.data?.detail || error.message));
    }
  };

  const openBookDialog = () => {
    setBookQuantity(1);
    setBookComment("");
    setBookLocation("");
    setShowBookDialog(true);
  };

  const handleBook = async () => {
    if (bookQuantity < 1) {
      toast.error("Ange ett giltigt antal");
      return;
    }
    if (!bookComment.trim()) {
      toast.error("Ange ordernummer/referens för bokningen");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/inventory/book`, {
        ean: item.ean,
        quantity: bookQuantity,
        comment: bookComment,
        location: bookLocation || null
      });
      toast.success(response.data.message);
      setShowBookDialog(false);
      fetchItem();
    } catch (error) {
      toast.error("Kunde inte boka: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/inventory/bookings/${bookingId}`);
      toast.success("Bokningen borttagen");
      fetchItem();
    } catch (error) {
      toast.error("Kunde inte ta bort bokning: " + (error.response?.data?.detail || error.message));
    }
  };

  const openScanOutDialog = () => {
    // Pre-select current location if only one exists
    if (productSummary && productSummary.locations.length === 1) {
      setScanOutLocation(productSummary.locations[0].lagerplats);
      setMaxScanOutQuantity(productSummary.locations[0].antal);
      setScanOutQuantity(1);
    } else if (productSummary && productSummary.locations.length > 1) {
      setScanOutLocation("");
      setScanOutQuantity(1);
      setMaxScanOutQuantity(1);
    } else {
      setScanOutLocation(item?.lagerplats || "");
      setMaxScanOutQuantity(item?.antal || 1);
      setScanOutQuantity(1);
    }
    setShowScanOutDialog(true);
  };

  const handleScanOutLocationChange = (location) => {
    setScanOutLocation(location);
    const loc = productSummary?.locations.find(l => l.lagerplats === location);
    if (loc) {
      setMaxScanOutQuantity(loc.antal);
      setScanOutQuantity(Math.min(scanOutQuantity, loc.antal));
    }
  };

  const handleScanOut = async () => {
    if (!scanOutLocation.trim()) {
      toast.error("Välj lagerplats att skanna ut från");
      return;
    }
    
    try {
      // Find the inventory item at the selected location
      const loc = productSummary?.locations.find(l => l.lagerplats === scanOutLocation);
      if (!loc) {
        toast.error("Kunde inte hitta produkten på vald plats");
        return;
      }
      
      // Call the scan-out endpoint
      const response = await axios.post(`${API}/inventory/scan-out`, {
        ean: item.ean,
        location: scanOutLocation,
        quantity: scanOutQuantity
      });
      
      // Check if blocked
      if (response.data.blocked) {
        toast.error(response.data.message);
        return;
      }
      
      // Show success with optional warning about unspecified bookings
      if (response.data.warning) {
        toast.success(response.data.message);
        toast.warning(response.data.warning, { duration: 5000 });
      } else {
        toast.success(`${scanOutQuantity} st utskannad från ${scanOutLocation}`);
      }
      
      setShowScanOutDialog(false);
      fetchItem();
    } catch (error) {
      toast.error("Kunde inte skanna ut: " + (error.response?.data?.detail || error.message));
    }
  };

  const formatDate = (isoString) => {
    if (!isoString || isoString === "1000-01-01") return "—";
    try {
      const date = new Date(isoString);
      if (date.getFullYear() < 2000) return "—";
      return date.toLocaleDateString('sv-SE');
    } catch {
      return "—";
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    try {
      const date = new Date(isoString);
      return date.toLocaleString('sv-SE', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "—";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navigation />
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navigation />
        <div className="flex items-center justify-center h-64">
          <p className="text-destructive">Produkten hittades inte</p>
        </div>
      </div>
    );
  }

  const availableForBooking = productSummary ? productSummary.available : item.antal;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/inventory">
                <Button variant="ghost" size="icon" data-testid="back-btn">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight" data-testid="item-title">
                  {item.artikelnummer || item.ean}
                </h1>
                <p className="text-sm text-muted-foreground truncate max-w-[250px]">
                  {item.produktnamn}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!editMode ? (
                <Button onClick={() => setEditMode(true)} data-testid="edit-btn">
                  Redigera
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); setFormData(item); }}>
                    Avbryt
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? "Sparar..." : "Spara"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* Stock Summary Card - Overview at top */}
          {productSummary && (
            <Card className="border-primary/30">
              <CardContent className="pt-6">
                <div className="grid grid-cols-3 gap-4 text-center mb-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Totalt</p>
                    <p className="text-3xl font-bold" data-testid="total-quantity">{productSummary.total_quantity}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Bokat</p>
                    <p className="text-3xl font-bold text-amber-500" data-testid="total-booked">{productSummary.total_booked}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Tillgängligt</p>
                    <p className="text-3xl font-bold text-green-500" data-testid="available-quantity">{productSummary.available}</p>
                  </div>
                </div>
                
                {/* Extended stock status from purchasing */}
                {stockStatus && (stockStatus.pending > 0 || stockStatus.ordered > 0 || stockStatus.uncertain > 0 || stockStatus.on_the_way > 0 || stockStatus.reserved > 0) && (
                  <div className="border-t pt-4">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 text-center">Inköpsstatus</p>
                    <div className="grid grid-cols-3 gap-3">
                      {stockStatus.pending > 0 && (
                        <Link to={`/purchasing/orders?search=${item?.artikelnummer || item?.ean}`} className="flex flex-col items-center p-2 rounded-md bg-red-500/10 hover:bg-red-500/20 transition-colors cursor-pointer">
                          <ShoppingBag className="h-4 w-4 text-red-500 mb-1" />
                          <span className="font-bold text-red-500">{stockStatus.pending}</span>
                          <span className="text-xs text-muted-foreground">Inlagda</span>
                        </Link>
                      )}
                      {stockStatus.ordered > 0 && (
                        <Link to={`/purchasing/orders?search=${item?.artikelnummer || item?.ean}`} className="flex flex-col items-center p-2 rounded-md bg-blue-500/10 hover:bg-blue-500/20 transition-colors cursor-pointer">
                          <Clock className="h-4 w-4 text-blue-500 mb-1" />
                          <span className="font-bold text-blue-500">{stockStatus.ordered}</span>
                          <span className="text-xs text-muted-foreground">Beställda</span>
                        </Link>
                      )}
                      {stockStatus.uncertain > 0 && (
                        <Link to={`/purchasing/orders?search=${item?.artikelnummer || item?.ean}`} className="flex flex-col items-center p-2 rounded-md bg-purple-500/10 hover:bg-purple-500/20 transition-colors cursor-pointer">
                          <HelpCircle className="h-4 w-4 text-purple-500 mb-1" />
                          <span className="font-bold text-purple-500">{stockStatus.uncertain}</span>
                          <span className="text-xs text-muted-foreground">Osäker</span>
                        </Link>
                      )}
                      {stockStatus.on_the_way > 0 && (
                        <Link to={`/purchasing/orders?search=${item?.artikelnummer || item?.ean}`} className="flex flex-col items-center p-2 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors cursor-pointer">
                          <Truck className="h-4 w-4 text-emerald-500 mb-1" />
                          <span className="font-bold text-emerald-500">{stockStatus.on_the_way}</span>
                          <span className="text-xs text-muted-foreground">På väg</span>
                        </Link>
                      )}
                      {stockStatus.reserved > 0 && (
                        <div 
                          className="flex flex-col items-center p-2 rounded-md bg-orange-500/10 hover:bg-orange-500/20 transition-colors cursor-pointer"
                          onClick={() => setShowReservationsDialog(true)}
                        >
                          <Bookmark className="h-4 w-4 text-orange-600 mb-1" />
                          <span className="font-bold text-orange-600">{stockStatus.reserved}</span>
                          <span className="text-xs text-muted-foreground">Reserverade</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Expected delivery info */}
                    {stockStatus.expected_week && (
                      <div className="mt-3 p-3 bg-green-500/10 rounded-md text-center">
                        <p className="text-xs text-muted-foreground">Förväntas levereras</p>
                        <p className="font-bold text-green-600">
                          Vecka {stockStatus.expected_week}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Quick order link */}
                {item && productSummary?.available <= 2 && (!stockStatus || (stockStatus.ordered === 0 && stockStatus.on_the_way === 0)) && (
                  <div className="border-t pt-4 mt-4">
                    <Link to={`/purchasing/orders?quickOrder=true&ean=${item.ean}&artikelnummer=${encodeURIComponent(item.artikelnummer || '')}`}>
                      <Button variant="outline" className="w-full gap-2 text-orange-500 border-orange-500 hover:bg-orange-500/10">
                        <ShoppingBag className="h-4 w-4" />
                        Beställ mer (lågt lager)
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* All Locations Card */}
          {productSummary && productSummary.locations.filter(l => (l.antal || 0) > 0 && l.lagerplats).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Alla lagerplatser ({productSummary.locations.filter(l => (l.antal || 0) > 0 && l.lagerplats).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {productSummary.locations.filter(l => (l.antal || 0) > 0 && l.lagerplats).map((loc) => (
                    <div
                      key={loc.id}
                      className="p-4 rounded-lg border bg-muted/50"
                    >
                      <p className="font-mono font-bold text-lg" data-testid={`location-${loc.lagerplats}`}>{loc.lagerplats}</p>
                      <p className="text-muted-foreground">{loc.antal} st</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Active Bookings Card */}
          {productSummary && productSummary.bookings.length > 0 && (
            <Card className="border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-500">
                  <Bookmark className="h-5 w-5" />
                  Aktiva bokningar ({productSummary.bookings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {productSummary.bookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-start justify-between p-4 rounded-lg bg-amber-500/10 border border-amber-500/20"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="border-amber-500 text-amber-500">
                            {booking.quantity} st
                          </Badge>
                          {booking.location && (
                            <Badge variant="secondary" className="font-mono">
                              {booking.location}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium">{booking.comment}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{formatDateTime(booking.booked_at)}</span>
                          {booking.booked_by && (
                            <>
                              <span>•</span>
                              <span>av {booking.booked_by}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => handleCancelBooking(booking.id)}
                        data-testid={`cancel-booking-${booking.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Info Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Produktinformation
                </CardTitle>
                <div className="flex items-center gap-3">
                  {item.created_at && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Skapad {formatDate(item.created_at)}
                    </span>
                  )}
                  <Switch
                    checked={showPurchasePrices}
                    onCheckedChange={setShowPurchasePrices}
                    data-testid="detail-purchase-prices-switch"
                    title="Visa inköpspriser"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {showPurchasePrices && (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3" data-testid="detail-purchase-prices">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">Inköpspriser per leverantör</p>
                  {detailPrices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Inga inköpspriser registrerade</p>
                  ) : (
                    <div className="space-y-1">
                      {detailPrices.map(p => {
                        const supName = detailSuppliers.find(s => s.id === p.supplier_id)?.company_name || "Okänd";
                        return (
                          <div key={p.id || p.supplier_id} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{supName}</span>
                            {p.unavailable ? (
                              <span className="text-red-600 text-xs">Otillgänglig</span>
                            ) : (
                              <span className="font-medium">
                                {dualPriceText(p.purchase_price || 0, p.currency || 'SEK')}
                                {p.chemical_tax > 0 && <span className="text-xs text-muted-foreground ml-2">+ kem. {p.chemical_tax}</span>}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {editMode ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>EAN</Label>
                      <div className="flex items-center h-10 px-3 rounded-md border bg-muted/50 text-muted-foreground font-mono">
                        {formData.ean || "—"}
                      </div>
                      <p className="text-xs text-muted-foreground">EAN kan inte ändras</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Artikelnummer</Label>
                      <Input
                        value={formData.artikelnummer || ""}
                        onChange={(e) => setFormData({...formData, artikelnummer: e.target.value.toUpperCase()})}
                        className="uppercase"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Produktnamn / Beskrivning</Label>
                    <Textarea
                      value={formData.produktnamn || ""}
                      onChange={(e) => setFormData({...formData, produktnamn: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tillverkare</Label>
                      <Popover open={manufacturerOpen} onOpenChange={setManufacturerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={manufacturerOpen}
                            className="w-full justify-between"
                          >
                            {formData.tillverkare || "Välj tillverkare..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Sök tillverkare..." />
                            <CommandList className="max-h-48">
                              <CommandEmpty>Ingen tillverkare hittad</CommandEmpty>
                              <CommandGroup>
                                {manufacturers.map((m) => (
                                  <CommandItem
                                    key={m}
                                    value={m}
                                    onSelect={() => {
                                      setFormData({...formData, tillverkare: m});
                                      setManufacturerOpen(false);
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", formData.tillverkare === m ? "opacity-100" : "opacity-0")} />
                                    {m}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Kategori</Label>
                      <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={categoryOpen}
                            className="w-full justify-between"
                          >
                            {formData.kategori || "Välj kategori..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Sök kategori..." />
                            <CommandList className="max-h-48">
                              <CommandEmpty>Ingen kategori hittad</CommandEmpty>
                              <CommandGroup>
                                {categories.map((c) => (
                                  <CommandItem
                                    key={c}
                                    value={c}
                                    onSelect={() => {
                                      setFormData({...formData, kategori: c});
                                      setCategoryOpen(false);
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", formData.kategori === c ? "opacity-100" : "opacity-0")} />
                                    {c}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Artikelnummer</p>
                      <p className="font-bold" data-testid="item-artikelnummer">{item.artikelnummer || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">EAN</p>
                      <p className="font-mono text-lg" data-testid="item-ean">{item.ean}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Beskrivning</p>
                    <p className="text-muted-foreground">{item.produktnamn || "—"}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Tillverkare</p>
                        <p>{item.tillverkare || "—"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Kategori</p>
                        <p>{item.kategori || "—"}</p>
                      </div>
                    </div>
                  </div>

                  <RecentEventsSection ean={item.ean} formatDateTime={formatDateTime} />
                </>
              )}
            </CardContent>
          </Card>

          {/* Actions Card - All actions in one place */}
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Åtgärder
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show reservation fix alert if there's a bug */}
              {hasReservationBug && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-3">
                  <div className="flex items-start gap-2 text-sm text-amber-600">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Det finns {reservations.length} reservation{reservations.length > 1 ? 'er' : ''} och {actualAvailable} tillgängliga produkter. Reservationerna har inte konverterats automatiskt.
                    </span>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={handleFixReservations}
                    disabled={fixingReservations}
                    data-testid="fix-reservations-btn"
                  >
                    {fixingReservations ? (
                      <span className="animate-spin mr-2">⏳</span>
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Konvertera reservationer till bokningar
                  </Button>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button 
                  variant="outline" 
                  onClick={openBookDialog} 
                  className="h-auto py-4 flex flex-col gap-2" 
                  data-testid="book-btn"
                >
                  <Bookmark className="h-6 w-6 text-amber-500" />
                  <span className="font-medium">Boka / Reservera</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={openMoveDialog} 
                  className="h-auto py-4 flex flex-col gap-2" 
                  data-testid="move-btn"
                >
                  <Move className="h-6 w-6 text-blue-500" />
                  <span className="font-medium">Flytta till annan plats</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/scan?ean=${item?.ean}&returnTo=/inventory/${item?.ean}`)}
                  className="h-auto py-4 flex flex-col gap-2" 
                  data-testid="scan-btn"
                >
                  <div className="flex items-center gap-1">
                    <ArrowDownToLine className="h-5 w-5 text-green-500" />
                    <span className="text-muted-foreground">/</span>
                    <ArrowUpFromLine className="h-5 w-5 text-red-500" />
                  </div>
                  <span className="font-medium">In/utleverans</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Delete Section - Admin only, only in edit mode */}
          {user?.role === "admin" && editMode && (
            <Card className="border-destructive/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <Trash2 className="h-5 w-5" />
                  Radera produkt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {productSummary?.total_quantity > 0 ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Produkten har {productSummary.total_quantity} st i lager. Nollställ först.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full text-orange-500 border-orange-500/30 hover:bg-orange-500/10"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/inventory/zero-out/${item.ean}`);
                          toast.success("Lagersaldo nollställt");
                          fetchItem();
                        } catch (error) {
                          toast.error("Kunde inte nollställa: " + (error.response?.data?.detail || error.message));
                        }
                      }}
                    >
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Nollställ lagersaldo ({productSummary?.total_quantity} st)
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Produkten har 0 i lager och kan raderas permanent.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Radera produkt permanent
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Move Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flytta produkt</DialogTitle>
            <DialogDescription>
              Flytta {item?.artikelnummer || item?.ean} till en ny lagerplats
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* From location */}
            <div className="space-y-2">
              <Label>Från lagerplats</Label>
              {productSummary && productSummary.locations.filter(l => (l.antal || 0) > 0).length > 1 ? (
                <Select value={moveFromLocation} onValueChange={handleFromLocationChange}>
                  <SelectTrigger data-testid="move-from-select">
                    <SelectValue placeholder="Välj lagerplats..." />
                  </SelectTrigger>
                  <SelectContent>
                    {productSummary.locations.filter(l => (l.antal || 0) > 0 && l.lagerplats).map((loc) => (
                      <SelectItem key={loc.id} value={loc.lagerplats || `_empty_${loc.id}`}>
                        {loc.lagerplats} ({loc.antal} st)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-mono font-bold text-lg">{moveFromLocation || productSummary?.locations?.find(l => (l.antal || 0) > 0)?.lagerplats || "—"}</p>
              )}
            </div>
            
            {/* To location */}
            <div className="space-y-2">
              <Label>Till lagerplats</Label>
              
              {/* Other locations where product exists (excluding source) */}
              {productSummary && productSummary.locations.filter(loc => loc.lagerplats !== moveFromLocation && (loc.antal || 0) > 0).length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Produkten finns på:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {productSummary.locations
                      .filter(loc => loc.lagerplats !== moveFromLocation && (loc.antal || 0) > 0)
                      .map(loc => (
                        <Button
                          key={loc.id}
                          variant={moveToLocation === loc.lagerplats ? "default" : "outline"}
                          size="sm"
                          className="gap-2 font-mono"
                          onClick={() => setMoveToLocation(loc.lagerplats)}
                          data-testid={`move-to-existing-${loc.lagerplats}`}
                        >
                          <MapPin className="h-3 w-3" />
                          {loc.lagerplats}
                          <Badge variant="secondary" className="ml-1 text-xs">
                            {loc.antal} st
                          </Badge>
                        </Button>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Recent locations (excluding source and existing locations) */}
              {recentLocations.filter(loc => 
                loc !== moveFromLocation && 
                !productSummary?.locations.some(l => l.lagerplats === loc && (l.antal || 0) > 0)
              ).length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Senast använda:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recentLocations
                      .filter(loc => 
                        loc !== moveFromLocation && 
                        !productSummary?.locations.some(l => l.lagerplats === loc && (l.antal || 0) > 0)
                      )
                      .slice(0, 5)
                      .map(loc => (
                        <Button
                          key={`recent-${loc}`}
                          variant={moveToLocation === loc ? "default" : "outline"}
                          size="sm"
                          className="gap-2 font-mono border-dashed"
                          onClick={() => setMoveToLocation(loc)}
                          data-testid={`move-to-recent-${loc}`}
                        >
                          <Clock className="h-3 w-3" />
                          {loc}
                        </Button>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Combobox for all locations */}
              <Popover open={moveToLocationOpen} onOpenChange={setMoveToLocationOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={moveToLocationOpen}
                    className="w-full justify-between font-mono"
                    data-testid="move-to-input"
                  >
                    {moveToLocation || "Välj eller sök lagerplats..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Sök lagerplats..." />
                    <CommandList className="max-h-48">
                      <CommandEmpty>Ingen lagerplats hittad</CommandEmpty>
                      <CommandGroup>
                        {locations.filter(l => l !== moveFromLocation).map((l) => (
                          <CommandItem
                            key={l}
                            value={l}
                            onSelect={() => {
                              setMoveToLocation(l);
                              setMoveToLocationOpen(false);
                            }}
                            className="font-mono"
                          >
                            <Check className={cn("mr-2 h-4 w-4", moveToLocation === l ? "opacity-100" : "opacity-0")} />
                            {l}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Quantity */}
            <div className="space-y-2">
              <Label>Antal att flytta (max {maxMoveQuantity})</Label>
              <Input
                type="number"
                min="1"
                max={maxMoveQuantity}
                value={moveQuantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setMoveQuantity('');
                  } else {
                    setMoveQuantity(Math.min(parseInt(val) || 1, maxMoveQuantity));
                  }
                }}
                onBlur={(e) => {
                  // Restore to 1 if empty on blur
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    setMoveQuantity(1);
                  }
                }}
                onFocus={(e) => e.target.select()}
                data-testid="move-quantity-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Avbryt</Button>
            <Button onClick={handleMove} data-testid="move-confirm-btn">Flytta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

          {/* Book Dialog */}
      <Dialog open={showBookDialog} onOpenChange={setShowBookDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Boka / Reservera</DialogTitle>
            <DialogDescription>
              Reservera {item?.artikelnummer || item?.ean} med en referens
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Available info */}
            <div className="p-3 rounded-lg bg-muted/50 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              <div className="text-sm">
                <span className="font-medium">{availableForBooking} st</span>
                <span className="text-muted-foreground"> tillgängligt för bokning</span>
              </div>
            </div>
            
            {/* Comment/Reference - moved to top */}
            <div className="space-y-2">
              <Label>Ordernummer / Referens *</Label>
              <Textarea
                value={bookComment}
                onChange={(e) => setBookComment(e.target.value)}
                rows={2}
                data-testid="book-comment-input"
              />
            </div>
            
            {/* Quantity - moved below reference */}
            <div className="space-y-2">
              <Label>Antal att boka</Label>
              <Input
                type="number"
                min="1"
                max={availableForBooking}
                value={bookQuantity}
                onChange={(e) => setBookQuantity(Math.min(parseInt(e.target.value) || 1, availableForBooking))}
                data-testid="book-quantity-input"
              />
            </div>
            
            {/* Optional: specific location */}
            {productSummary && productSummary.locations.length > 1 && (
              <div className="space-y-2">
                <Label>Specifik lagerplats (valfritt)</Label>
                <Select value={bookLocation || "all"} onValueChange={(v) => setBookLocation(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="book-location-select">
                    <SelectValue placeholder="Alla platser..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alla platser</SelectItem>
                    {productSummary.locations.filter(l => l.lagerplats).map((loc) => (
                      <SelectItem key={loc.id} value={loc.lagerplats}>
                        {loc.lagerplats} ({loc.antal} st)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookDialog(false)}>Avbryt</Button>
            <Button onClick={handleBook} data-testid="book-confirm-btn">Boka</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Out Dialog */}
      <Dialog open={showScanOutDialog} onOpenChange={setShowScanOutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Utleverans</DialogTitle>
            <DialogDescription>
              Ta ut {item?.artikelnummer || item?.ean} från lagret. Detta tar bort produkten och loggas i historiken.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* From location */}
            <div className="space-y-2">
              <Label>Från lagerplats</Label>
              {productSummary && productSummary.locations.length > 1 ? (
                <Select value={scanOutLocation} onValueChange={handleScanOutLocationChange}>
                  <SelectTrigger data-testid="scan-out-location-select">
                    <SelectValue placeholder="Välj lagerplats..." />
                  </SelectTrigger>
                  <SelectContent>
                    {productSummary.locations.filter(l => l.lagerplats).map((loc) => (
                      <SelectItem key={loc.id} value={loc.lagerplats}>
                        {loc.lagerplats} ({loc.antal} st)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-mono font-bold text-lg">{scanOutLocation || item?.lagerplats}</p>
              )}
            </div>
            
            {/* Quantity */}
            <div className="space-y-2">
              <Label>Antal att skanna ut (max {maxScanOutQuantity})</Label>
              <Input
                type="number"
                min="1"
                max={maxScanOutQuantity}
                value={scanOutQuantity}
                onChange={(e) => setScanOutQuantity(Math.min(parseInt(e.target.value) || 1, maxScanOutQuantity))}
                data-testid="scan-out-quantity-input"
              />
            </div>
            
            {/* Warning */}
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-destructive">
                Produkten tas bort från lagersaldot och kan inte ångras.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScanOutDialog(false)}>Avbryt</Button>
            <Button 
              onClick={handleScanOut} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="scan-out-confirm-btn"
            >
              Utleverans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Product Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Radera produkt permanent
            </DialogTitle>
            <DialogDescription>
              Du håller på att radera <strong>{item?.artikelnummer || item?.ean}</strong> permanent. 
              Detta tar bort alla lagerplatser för denna produkt och kan inte ångras.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive font-medium">
                Varning: Denna åtgärd kan inte ångras!
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Produkten raderas från lagersystemet</li>
                <li>• Historik bevaras för spårbarhet</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleDeleteProduct}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Raderar..." : "Radera permanent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reservations Dialog */}
      <Dialog open={showReservationsDialog} onOpenChange={setShowReservationsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-500">
              <ShoppingBag className="h-5 w-5" />
              Reservationer ({reservations.length})
            </DialogTitle>
            <DialogDescription>
              Produkter reserverade för kommande kundorder. Reservationer omvandlas automatiskt till bokningar när produkten kommer in i lager.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto py-2">
            {reservations.map((res) => (
              <div
                key={res.id}
                className="flex items-start justify-between gap-3 p-4 rounded-lg bg-orange-500/10 border border-orange-500/20"
                data-testid={`reservation-${res.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className="border-orange-500 text-orange-500">
                      {res.quantity} st
                    </Badge>
                    <Badge variant="secondary" className="font-mono text-xs">
                      Order: {res.customer_order_number || "Okänd"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span>{formatDateTime(res.created_at)}</span>
                    {res.purchase_order_number && (
                      <span>· Inköpsorder: <span className="font-mono">{res.purchase_order_number}</span></span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 shrink-0"
                  onClick={async () => {
                    if (!window.confirm(`Radera reservation för kundorder ${res.customer_order_number || "Okänd"} (${res.quantity} st)?\n\nDetta kan inte ångras.`)) return;
                    try {
                      await axios.delete(`${API}/purchasing/reservations/${res.id}`);
                      toast.success("Reservation borttagen");
                      fetchItem();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Kunde inte ta bort reservation");
                    }
                  }}
                  title="Radera reservation"
                  data-testid={`delete-reservation-${res.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          
          {/* Show fix button only when there's a reservation bug */}
          {hasReservationBug && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2">
              <div className="flex items-start gap-2 text-sm text-amber-600">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Det finns {actualAvailable} tillgängliga produkter i lager men reservationerna har inte konverterats automatiskt.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full border-amber-500 text-amber-600 hover:bg-amber-500/10"
                onClick={handleFixReservations}
                disabled={fixingReservations}
              >
                {fixingReservations ? (
                  <span className="animate-spin mr-2">⏳</span>
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Konvertera reservationer till bokningar
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReservationsDialog(false)}>
              Stäng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecentEventsSection({ ean, formatDateTime }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/api/inventory/recent-events/${ean}?limit=5`);
        setEvents(res.data.events || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchEvents();
  }, [ean, API]);

  const actionLabels = {
    IN: { label: "Inleverans", color: "text-green-500", icon: "+" },
    OUT: { label: "Utleverans", color: "text-red-400", icon: "-" },
    BOOK: { label: "Bokad", color: "text-yellow-500", icon: "B" },
    UNBOOK: { label: "Avbokad", color: "text-blue-400", icon: "A" },
    MOVE: { label: "Flyttad", color: "text-purple-400", icon: "F" },
    INVENTORY: { label: "Inventering", color: "text-amber-500", icon: "I" },
    ZERO: { label: "Nollställd", color: "text-red-500", icon: "0" },
  };

  if (loading) return <div className="pt-4 border-t text-xs text-muted-foreground">Laddar händelser...</div>;
  if (!events.length) return null;

  return (
    <div className="pt-4 border-t" data-testid="recent-events">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Senaste händelser</p>
      <div className="space-y-1.5">
        {events.map((e, i) => {
          const cfg = actionLabels[e.action_type] || { label: e.action_type, color: "text-muted-foreground", icon: "?" };
          // Build location/route text per action type
          let locationText = "";
          if (e.action_type === "MOVE") {
            const from = e.from_location || "";
            const to = e.to_location || "";
            if (from && to) locationText = `${from} → ${to}`;
            else if (e.location) locationText = e.location;
          } else if (e.action_type === "OUT") {
            locationText = e.from_location || e.location || "";
            if (locationText) locationText = `från ${locationText}`;
          } else if (e.action_type === "IN") {
            locationText = e.to_location || e.location || "";
            if (locationText) locationText = `till ${locationText}`;
          } else if (e.location) {
            locationText = e.location;
          }
          return (
            <div key={e.id || i} className="flex items-center gap-2 text-sm" data-testid={`event-${i}`}>
              <span className={`w-5 h-5 rounded text-xs font-bold flex items-center justify-center shrink-0 ${cfg.color} bg-current/10`}
                style={{ backgroundColor: `color-mix(in srgb, currentColor 12%, transparent)` }}>
                {cfg.icon}
              </span>
              <span className={`font-medium ${cfg.color} w-20 shrink-0`}>{cfg.label}</span>
              <span className="text-muted-foreground shrink-0">{e.quantity || ""} st</span>
              {locationText && (
                <span className="text-foreground/80 font-mono text-xs truncate" title={locationText}>
                  {locationText}
                </span>
              )}
              {e.reference && <span className="text-muted-foreground truncate max-w-[140px]">— {e.reference}</span>}
              <span className="ml-auto text-xs text-muted-foreground shrink-0">{formatDateTime(e.timestamp)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

